// @flow
import { List, Map as ImmutableMap, Record, Set } from 'immutable';
import type { RecordFactory, RecordOf } from 'immutable';

import type { QcsSyncResourceStoreProps } from 'data/qcsSyncResourceTypes';
import ExtendableError from 'es6-error';

type InstitutionLoginAggregatorProps = {
  channel: ?string,

  aggStatus: ?string,
  aggStatusCode: ?string,
  aggStatusDetail: ?string,

  lastStatusUpdatedAt: ?string,

  lastRefreshSuccessfulAt: ?string,
  lastRefreshAttemptedAt: ?string,
}
export type InstitutionLoginAggregator = RecordOf<InstitutionLoginAggregatorProps>;
export const mkInstitutionLoginAggregator: RecordFactory<InstitutionLoginAggregatorProps> =
  new Record({
    channel: null,

    aggStatus: null,
    aggStatusCode: null,
    aggStatusDetail: null,

    lastStatusUpdatedAt: null,

    lastRefreshSuccessfulAt: null,
    lastRefreshAttemptedAt: null,
  });

type CredentialProps = {
  key: string,
  value: string,
};
export type Credential = RecordOf<CredentialProps>;
export const mkCredential :RecordFactory<CredentialProps> =
  new Record({
    key: 'unknown',
    value: 'unknown',
  });

type InstitutionLoginProps = {
  id: ?string,
  name: ?string,

  institutionId: string,
  brandingId: ?string,
  cpInstitutionId: ?string,

  ewcLive: boolean,

  cpSetupMode: null | 'DISCOVER_ACCOUNTS_ONLY' | 'SKIP_ACCOUNT_DISCOVERY' | 'UPDATE_CREDENTIALS',
  credentials: ?List<Credential>,

  aggregators: ?List<InstitutionLoginAggregator>,

  isDeleted: boolean,
};
export type InstitutionLogin = RecordOf<InstitutionLoginProps>;
export const mkInstitutionLogin :RecordFactory<InstitutionLoginProps> =
  new Record({
    id: '0',
    name: 'unknown',

    institutionId: '0',
    brandingId: null,
    cpInstitutionId: null,

    ewcLive: false,

    cpSetupMode: null,
    credentials: null,

    aggregators: null,

    isDeleted: false,
  });

type InstitutionLoginsStoreProps = QcsSyncResourceStoreProps & {
  isRefreshingAll: boolean,
  idsRefreshing: Set<string>,

  idsRefreshingWithUserActionsPending: Set<string>,
  idsRefreshingWithCancelledUserActions: Set<string>,

  credentialBlobsForRefreshingLogins: ImmutableMap<string, string>,
  credentialsForRefreshingLogins: ImmutableMap<string, List<Credential>>,
}
export const mkInstitutionLoginsStore :RecordFactory<InstitutionLoginsStoreProps> =
  new Record({
    resourcesById: new ImmutableMap(),
    lastSyncDate: null,

    loadPending: true,
    isLoading: false,

    isRefreshingAll: false,
    idsRefreshing: Set(),

    idsRefreshingWithUserActionsPending: Set(),
    idsRefreshingWithCancelledUserActions: Set(),

    credentialBlobsForRefreshingLogins: ImmutableMap(),
    credentialsForRefreshingLogins: ImmutableMap(),
  });
export type InstitutionLoginsStore = RecordOf<InstitutionLoginsStoreProps>;

export type getInstitutionsPayload = {
  institutionLogins: Array<InstitutionLogin>,
  replace: boolean,
  loadPending: boolean,
  lastSyncDate: string,
}

// ================================================================================================
// MfaChallenge & MfaResponse
// ================================================================================================

type MfaAnswerChoiceProps = {
  displayText: string,
}
export type MfaAnswerChoice = RecordOf<MfaAnswerChoiceProps>;
export const mkMfaAnswerChoice: RecordFactory<MfaAnswerChoiceProps> =
  Record({
    displayText: 'unknown',
  });

type MfaChallengeItemProps = {
  type: string,

  key: string,
  sequence: number,

  base64Image: ?string,
  question: string,
  answerChoices: ?List<MfaAnswerChoice>,
}
export type MfaChallengeItem = RecordOf<MfaChallengeItemProps>;
export const mkMfaChallengeItem: RecordFactory<MfaChallengeItemProps> =
  Record({
    type: 'QUESTION',

    key: '0',
    sequence: 0,

    base64Image: null,
    question: 'unknown',
    answerChoices: null,
  });


type MfaChallengeProps = {
  institutionLoginId: string,
  challenges: List<MfaChallengeItem>,

  submitUrl: string,
}
export type MfaChallenge = RecordOf<MfaChallengeProps>;
export const mkMfaChallenge: RecordFactory<MfaChallengeProps> =
  Record({
    institutionLoginId: '0',
    challenges: List(),

    submitUrl: '',
  });


type MfaAnswerProps = {
  id: string,
  value: string,
};
export type MfaAnswer = RecordOf<MfaAnswerProps>;
export const mkMfaAnswer: RecordFactory<MfaAnswerProps> =
  Record({
    id: '0',
    value: 'unknown',
  });


type MfaResponseProps = {
  institutionLoginId: string,
  answers: List<MfaAnswer>,
  submitUrl: string,
}
export type MfaResponse = RecordOf<MfaResponseProps>;
export const mkMfaResponse: RecordFactory<MfaResponseProps> =
  Record({
    institutionLoginId: '0',
    answers: List(),
    submitUrl: 'need_to_set_submit_url',
  });

export class RefreshAccountsMfaFormSubmitError extends ExtendableError {
  constructor(institutionLoginId: String, message: String) {
    super(message);
    this.institutionLoginId = institutionLoginId;
  }
}
